import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { getAllBrackets } from '../bracketCache';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';

const C = {
  bg:            '#0D1B2A',
  bgCard:        '#13223A',
  bgDeep:        '#0A1520',
  bgHover:       '#1E3550',
  border:        'rgba(255,255,255,0.06)',
  orange:        '#FF6B00',
  orangeDim:     'rgba(255,107,0,0.15)',
  orangeBorder:  'rgba(255,107,0,0.35)',
  textPrimary:   '#FFFFFF',
  textSecondary: '#8FA3C0',
  textMuted:     '#5A7A9A',
  green:         '#00C851',
  greenDim:      'rgba(0,200,81,0.12)',
  greenBorder:   'rgba(0,200,81,0.3)',
  blue:          '#3B82F6',
  gold:          '#FFD700',
  goldDim:       'rgba(255,215,0,0.12)',
  goldBorder:    'rgba(255,215,0,0.4)',
  goldGlow:      'rgba(255,215,0,0.15)',
};
const font = `'SF Pro Display',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;

function getLocalDateString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ── BADGE ICON SVGs ───────────────────────────────────────────────────────────
// Custom SVG icons for each badge key
const BADGE_ICONS = {
  first_pick: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(255,107,0,0.2)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#FF6B00' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <path d="M14 7l1.8 5.4h5.7l-4.6 3.3 1.8 5.4L14 18l-4.7 3.1 1.8-5.4-4.6-3.3h5.7z" fill={earned ? '#FF6B00' : 'rgba(255,255,255,0.15)'}/>
    </svg>
  ),
  hat_trick: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(255,107,0,0.2)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#FF6B00' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <rect x="7" y="16" width="14" height="2" rx="1" fill={earned ? '#FF6B00' : 'rgba(255,255,255,0.15)'}/>
      <path d="M10 16V11c0-2.2 1.8-4 4-4s4 1.8 4 4v5" stroke={earned ? '#FF6B00' : 'rgba(255,255,255,0.15)'} strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="11" y="18" width="6" height="3" rx="1" fill={earned ? '#FF6B00' : 'rgba(255,255,255,0.1)'}/>
    </svg>
  ),
  on_fire: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(255,107,0,0.2)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#FF6B00' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <path d="M14 6s5 5 5 9a5 5 0 01-10 0c0-2 1-4 2-5-0.2 1.5 0.5 3 1.5 3.5C12 11 14 6 14 6z" fill={earned ? '#FF6B00' : 'rgba(255,255,255,0.15)'}/>
      <path d="M14 19a2.5 2.5 0 000-5c-.5 0-1 .2-1.4.5C13 14 14 12 14 12s2 2.5 2 4a2 2 0 01-4 0" fill={earned ? '#FFD700' : 'rgba(255,255,255,0.1)'}/>
    </svg>
  ),
  week_warrior: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#F59E0B' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <path d="M14 7l1 4h4l-3 2.5 1 4-3-2.2-3 2.2 1-4L9 11h4z" fill={earned ? '#F59E0B' : 'rgba(255,255,255,0.15)'}/>
      <path d="M14 20v1M10 19l-.7.7M18 19l.7.7" stroke={earned ? '#F59E0B' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  perfect_week: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? C.goldDim : 'rgba(255,255,255,0.04)'} stroke={earned ? C.gold : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <circle cx="14" cy="14" r="5" fill="none" stroke={earned ? C.gold : 'rgba(255,255,255,0.15)'} strokeWidth="1.5"/>
      <path d="M11 14l2 2 4-4" stroke={earned ? C.gold : 'rgba(255,255,255,0.15)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {[0,1,2,3,4,5,6,7].map(i => (
        <circle key={i} cx={14 + 10 * Math.cos(i * Math.PI / 4)} cy={14 + 10 * Math.sin(i * Math.PI / 4)} r="1" fill={earned ? C.gold : 'rgba(255,255,255,0.1)'}/>
      ))}
    </svg>
  ),
  monthly: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#3B82F6' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <rect x="8" y="9" width="12" height="11" rx="2" stroke={earned ? '#3B82F6' : 'rgba(255,255,255,0.15)'} strokeWidth="1.5" fill="none"/>
      <path d="M8 13h12" stroke={earned ? '#3B82F6' : 'rgba(255,255,255,0.1)'} strokeWidth="1"/>
      <path d="M11 7v3M17 7v3" stroke={earned ? '#3B82F6' : 'rgba(255,255,255,0.15)'} strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="11" y="16" width="2" height="2" rx=".5" fill={earned ? '#3B82F6' : 'rgba(255,255,255,0.1)'}/>
      <rect x="14" y="16" width="2" height="2" rx=".5" fill={earned ? '#3B82F6' : 'rgba(255,255,255,0.1)'}/>
    </svg>
  ),
  century: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? C.goldDim : 'rgba(255,255,255,0.04)'} stroke={earned ? C.gold : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <text x="14" y="18" textAnchor="middle" fontSize="10" fontWeight="800" fill={earned ? C.gold : 'rgba(255,255,255,0.15)'} fontFamily="Arial">100</text>
    </svg>
  ),
  legend: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? C.goldDim : 'rgba(255,255,255,0.04)'} stroke={earned ? C.gold : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <path d="M14 8c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6 2.7-6 6-6z" stroke={earned ? C.gold : 'rgba(255,255,255,0.15)'} strokeWidth="1.2" fill="none"/>
      <path d="M11 14l2 2 4-4" stroke={earned ? C.gold : 'rgba(255,255,255,0.15)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 5v2M14 21v2M5 14h2M21 14h2" stroke={earned ? C.gold : 'rgba(255,255,255,0.08)'} strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  ),
  commentator: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(0,200,81,0.15)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#00C851' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <path d="M8 10h12v8H16l-2 2-2-2H8V10z" stroke={earned ? '#00C851' : 'rgba(255,255,255,0.15)'} strokeWidth="1.5" fill={earned ? 'rgba(0,200,81,0.1)' : 'none'} strokeLinejoin="round"/>
      <path d="M11 14h6M11 17h4" stroke={earned ? '#00C851' : 'rgba(255,255,255,0.1)'} strokeWidth="1" strokeLinecap="round"/>
    </svg>
  ),
  hot_take: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#EF4444' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <path d="M14 7c2 2 4 4 4 7a4 4 0 01-8 0c0-1.5.5-3 1-4-.1 1 .3 2 1 2.5C12 10 14 7 14 7z" fill={earned ? '#EF4444' : 'rgba(255,255,255,0.15)'}/>
      <path d="M14 19a2 2 0 000-4c-.4 0-.8.1-1.1.4.4-.8 1.1-1.4 1.1-1.4s1.5 2 1.5 3a1.5 1.5 0 01-3 0" fill={earned ? '#FF6B00' : 'rgba(255,255,255,0.1)'}/>
    </svg>
  ),
  debater: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(139,92,246,0.15)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#8B5CF6' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <path d="M8 9h8v6H14l-1.5 1.5L11 15H8V9z" stroke={earned ? '#8B5CF6' : 'rgba(255,255,255,0.15)'} strokeWidth="1.2" fill={earned ? 'rgba(139,92,246,0.1)' : 'none'}/>
      <path d="M12 15v1.5h8V11h-4" stroke={earned ? '#8B5CF6' : 'rgba(255,255,255,0.1)'} strokeWidth="1.2" fill="none"/>
    </svg>
  ),
  with_people: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#3B82F6' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <circle cx="11" cy="12" r="2.5" stroke={earned ? '#3B82F6' : 'rgba(255,255,255,0.15)'} strokeWidth="1.2" fill="none"/>
      <circle cx="17" cy="12" r="2.5" stroke={earned ? '#3B82F6' : 'rgba(255,255,255,0.15)'} strokeWidth="1.2" fill="none"/>
      <path d="M7 20c0-2.2 1.8-4 4-4h6c2.2 0 4 1.8 4 4" stroke={earned ? '#3B82F6' : 'rgba(255,255,255,0.15)'} strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    </svg>
  ),
  contrarian: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#F59E0B' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <circle cx="14" cy="14" r="5" stroke={earned ? '#F59E0B' : 'rgba(255,255,255,0.15)'} strokeWidth="1.2" fill="none"/>
      <circle cx="14" cy="14" r="2" fill={earned ? '#F59E0B' : 'rgba(255,255,255,0.15)'}/>
      <path d="M14 7v2M14 19v2M7 14h2M19 14h2M9.1 9.1l1.4 1.4M17.5 17.5l1.4 1.4M9.1 18.9l1.4-1.4M17.5 10.5l1.4-1.4" stroke={earned ? '#F59E0B' : 'rgba(255,255,255,0.08)'} strokeWidth="1" strokeLinecap="round"/>
    </svg>
  ),
  oracle: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#8B5CF6' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <circle cx="14" cy="13" r="5" fill={earned ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.05)'} stroke={earned ? '#8B5CF6' : 'rgba(255,255,255,0.15)'} strokeWidth="1.2"/>
      <path d="M11 20h6" stroke={earned ? '#8B5CF6' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="14" cy="13" r="2" fill={earned ? '#8B5CF6' : 'rgba(255,255,255,0.1)'}/>
      <path d="M14 8v2M11.5 10.5l1.5 1.5" stroke={earned ? 'rgba(139,92,246,0.6)' : 'rgba(255,255,255,0.05)'} strokeWidth="1" strokeLinecap="round"/>
    </svg>
  ),
  sports_fan: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(255,107,0,0.15)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#FF6B00' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <circle cx="14" cy="14" r="5" stroke={earned ? '#FF6B00' : 'rgba(255,255,255,0.15)'} strokeWidth="1.2" fill="none"/>
      <path d="M14 9v10M9 14h10" stroke={earned ? '#FF6B00' : 'rgba(255,255,255,0.08)'} strokeWidth="1" strokeLinecap="round"/>
      <path d="M11 11l6 6M17 11l-6 6" stroke={earned ? '#FF6B00' : 'rgba(255,255,255,0.08)'} strokeWidth="1" strokeLinecap="round"/>
    </svg>
  ),
  cinephile: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#EF4444' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <rect x="8" y="10" width="12" height="9" rx="1.5" stroke={earned ? '#EF4444' : 'rgba(255,255,255,0.15)'} strokeWidth="1.2" fill="none"/>
      <path d="M11 10V8M17 10V8M9 13h10M9 16h10" stroke={earned ? '#EF4444' : 'rgba(255,255,255,0.1)'} strokeWidth="1" strokeLinecap="round"/>
    </svg>
  ),
  music_head: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? 'rgba(0,200,81,0.15)' : 'rgba(255,255,255,0.04)'} stroke={earned ? '#00C851' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <path d="M11 18V11l8-2v7" stroke={earned ? '#00C851' : 'rgba(255,255,255,0.15)'} strokeWidth="1.2" strokeLinecap="round" fill="none"/>
      <circle cx="11" cy="18" r="2" fill={earned ? '#00C851' : 'rgba(255,255,255,0.1)'}/>
      <circle cx="19" cy="16" r="2" fill={earned ? '#00C851' : 'rgba(255,255,255,0.1)'}/>
    </svg>
  ),
  culture_vulture: ({ size = 28, earned }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill={earned ? C.goldDim : 'rgba(255,255,255,0.04)'} stroke={earned ? C.gold : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/>
      <path d="M14 8l1.5 4.5H20l-3.7 2.7 1.4 4.3L14 17l-3.7 2.5 1.4-4.3L8 12.5h4.5z" fill={earned ? C.gold : 'rgba(255,255,255,0.15)'}/>
    </svg>
  ),
};

// Category accent colors
const CAT_COLORS = {
  Participation: '#FF6B00',
  Social:        '#8B5CF6',
  Accuracy:      '#F59E0B',
  Explorer:      '#00C851',
};

// Rarity: 'common' | 'rare' | 'legendary'
const BADGE_RARITY = {
  first_pick:'common', hat_trick:'common', commentator:'common', with_people:'common',
  on_fire:'rare', week_warrior:'rare', perfect_week:'rare', hot_take:'rare',
  debater:'rare', generous:'rare', contrarian:'rare', sports_fan:'rare', cinephile:'rare', music_head:'rare',
  monthly:'legendary', century:'legendary', culture_vulture:'legendary',
  oracle:'legendary', legend:'legendary',
};

const BADGES = [
  { key:'first_pick',      label:'First Pick',       icon:'🥳', pts:5,   category:'Participation', desc:'Submit your first bracket',               req:(s)=> s.total >= 1,    progress:(s)=> ({ val: Math.min(s.total,1),    max:1   }) },
  { key:'hat_trick',       label:'Hat Trick',         icon:'🎩', pts:10,  category:'Participation', desc:'Submit 3 brackets',                       req:(s)=> s.total >= 3,    progress:(s)=> ({ val: Math.min(s.total,3),    max:3   }) },
  { key:'on_fire',         label:'On Fire',            icon:'🔥', pts:15,  category:'Participation', desc:'5-day submission streak',                 req:(s)=> s.streak >= 5,   progress:(s)=> ({ val: Math.min(s.streak,5),   max:5   }) },
  { key:'week_warrior',    label:'Week Warrior',       icon:'⚡', pts:25,  category:'Participation', desc:'7-day submission streak',                 req:(s)=> s.streak >= 7,   progress:(s)=> ({ val: Math.min(s.streak,7),   max:7   }) },
  { key:'perfect_week',    label:'Perfect Week',       icon:'🏅', pts:30,  category:'Participation', desc:'Play all 7 days in a calendar week',      req:(s)=> s.perfectWeek >= 1, progress:(s)=> ({ val: Math.min(s.perfectWeek,1), max:1 }) },
  { key:'monthly',         label:'Monthly',            icon:'📅', pts:40,  category:'Participation', desc:'Submit 30 brackets in a calendar month',  req:(s)=> s.monthly >= 30, progress:(s)=> ({ val: Math.min(s.monthly,30), max:30  }) },
  { key:'century',         label:'Century',            icon:'💯', pts:75,  category:'Participation', desc:'Submit 100 total brackets',               req:(s)=> s.total >= 100,  progress:(s)=> ({ val: Math.min(s.total,100),  max:100 }) },
  { key:'legend',          label:'Legend',             icon:'🦁', pts:100, category:'Participation', desc:'Submit 365 total brackets',               req:(s)=> s.total >= 365,  progress:(s)=> ({ val: Math.min(s.total,365),  max:365 }) },
  { key:'commentator',     label:'Commentator',        icon:'💬', pts:5,   category:'Social',        desc:'Post your first comment',                 req:(s)=> s.comments >= 1,    progress:(s)=> ({ val: Math.min(s.comments,1),   max:1   }) },
  { key:'hot_take',        label:'Hot Take',            icon:'🌶️', pts:20,  category:'Social',        desc:'Get 10 likes on a single comment',        req:(s)=> s.maxLikes >= 10,   progress:(s)=> ({ val: Math.min(s.maxLikes,10),  max:10  }) },
  { key:'debater',         label:'Debater',             icon:'🗣️', pts:30,  category:'Social',        desc:'Post 25 comments',                        req:(s)=> s.comments >= 25,   progress:(s)=> ({ val: Math.min(s.comments,25),  max:25  }) },
  { key:'generous',        label:'Generous',            icon:'🎁', pts:25,  category:'Social',        desc:'Gift Pro to a friend',                    req:(s)=> (s.giftsGiven||0) >= 1, progress:(s)=> ({ val: Math.min(s.giftsGiven||0,1), max:1 }) },
  { key:'with_people',     label:'With the People',    icon:'👥', pts:15,  category:'Accuracy',      desc:'80%+ crowd agreement on a bracket',       req:(s)=> s.crowdMatch >= 1,  progress:(s)=> ({ val: Math.min(s.crowdMatch,1),  max:1   }) },
  { key:'contrarian',      label:'Contrarian',         icon:'🎯', pts:35,  category:'Accuracy',      desc:'Pick against the majority 10+ times',     req:(s)=> s.contrarian >= 10, progress:(s)=> ({ val: Math.min(s.contrarian,10), max:10  }) },
  { key:'oracle',          label:'Oracle',              icon:'🔮', pts:50,  category:'Accuracy',      desc:'Pick the overall winner correctly 5 times',req:(s)=> s.oracle >= 5,     progress:(s)=> ({ val: Math.min(s.oracle,5),     max:5   }) },
  { key:'sports_fan',      label:'Sports Fan',         icon:'🥇', pts:20,  category:'Explorer',      desc:'Submit 10 Sports brackets',               req:(s)=> (s.byCategory['Sports']||0) >= 10,  progress:(s)=> ({ val: Math.min(s.byCategory['Sports']||0,10), max:10 }) },
  { key:'cinephile',       label:'Cinephile',           icon:'🏆', pts:20,  category:'Explorer',      desc:'Submit 5 Movies brackets',                req:(s)=> (s.byCategory['Movies']||0) >= 5,   progress:(s)=> ({ val: Math.min(s.byCategory['Movies']||0,5),  max:5  }) },
  { key:'music_head',      label:'Music Head',          icon:'🎵', pts:20,  category:'Explorer',      desc:'Submit 5 Music brackets',                 req:(s)=> (s.byCategory['Music']||0) >= 5,    progress:(s)=> ({ val: Math.min(s.byCategory['Music']||0,5),   max:5  }) },
  { key:'culture_vulture', label:'Culture Vulture',     icon:'🦅', pts:50,  category:'Explorer',      desc:'Submit a bracket in all 8 categories',    req:(s)=> s.categoriesPlayed >= 8,            progress:(s)=> ({ val: Math.min(s.categoriesPlayed,8),         max:8  }) },
];

const TOTAL_POSSIBLE_PTS = BADGES.reduce((sum, b) => sum + b.pts, 0);
const CATEGORY_FILTERS   = ['All','Sports','Music','Movies','TV','Pop Culture','Gaming','Food','History'];
const TIME_FILTERS       = ['All time','This month','This week'];
const DAY_LABELS         = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTH_NAMES        = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── BADGE UNLOCK ANIMATION ────────────────────────────────────────────────────
function BadgeUnlockBurst({ badge, onDone }) {
  const [visible, setVisible] = useState(true);
  const rarity = BADGE_RARITY[badge.key] || 'common';
  const duration = rarity === 'legendary' ? 4500 : rarity === 'rare' ? 3500 : 2500;

  useEffect(() => {
    const t = setTimeout(() => { setVisible(false); setTimeout(onDone, 500); }, duration);
    return () => clearTimeout(t);
  }, [onDone, duration]);

  const IconComp = BADGE_ICONS[badge.key];
  const catColor = CAT_COLORS[badge.category] || C.orange;
  const rarityColors = { common: catColor, rare: '#F59E0B', legendary: '#FFD700' };
  const glowColor = rarityColors[rarity];
  const ringCount = rarity === 'legendary' ? 5 : rarity === 'rare' ? 4 : 3;
  const starCount = rarity === 'legendary' ? 12 : rarity === 'rare' ? 10 : 8;
  const iconSize  = rarity === 'legendary' ? 100 : rarity === 'rare' ? 90 : 80;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      backgroundColor: rarity === 'legendary' ? 'rgba(0,0,0,0.95)' : 'rgba(0,0,0,0.88)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: font,
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.5s',
    }}>
      <style>{`
        @keyframes badgePop { 0%{transform:scale(0.2);opacity:0} 60%{transform:scale(1.2)} 80%{transform:scale(0.92)} 100%{transform:scale(1);opacity:1} }
        @keyframes badgeRing { 0%{transform:scale(0.6);opacity:0.9} 100%{transform:scale(3);opacity:0} }
        @keyframes badgeStar { 0%{opacity:1;transform:translate(0,0) scale(1)} 100%{opacity:0;transform:translate(var(--tx),var(--ty)) scale(0)} }
        @keyframes legendGlow { 0%,100%{box-shadow:0 0 30px ${glowColor}44} 50%{box-shadow:0 0 80px ${glowColor}88,0 0 120px ${glowColor}44} }
        @keyframes rarityLabel { 0%{transform:translateY(10px);opacity:0} 100%{transform:translateY(0);opacity:1} }
      `}</style>

      {/* Rings */}
      {Array.from({length:ringCount}).map((_, i) => (
        <div key={i} style={{
          position: 'absolute', width: '120px', height: '120px',
          borderRadius: '50%',
          border: '2px solid ' + glowColor,
          animation: 'badgeRing 1.4s ease-out ' + (i * 0.25) + 's forwards',
          opacity: 0,
        }}/>
      ))}

      {/* Burst particles */}
      {Array.from({length:starCount}).map((_, i) => {
        const angle = (i / starCount) * Math.PI * 2;
        const dist  = rarity === 'legendary' ? 100 : 75;
        const tx    = Math.cos(angle) * dist;
        const ty    = Math.sin(angle) * dist;
        return (
          <div key={i} style={{
            position: 'absolute', top:'50%', left:'50%',
            width: rarity==='legendary'?'8px':'6px',
            height: rarity==='legendary'?'8px':'6px',
            borderRadius:'50%', backgroundColor:glowColor,
            marginTop:'-4px', marginLeft:'-4px',
            '--tx': tx+'px', '--ty': ty+'px',
            animation: 'badgeStar 0.9s ease-out 0.1s forwards',
            opacity: 0,
          }}/>
        );
      })}

      {/* Rarity label */}
      {rarity !== 'common' && (
        <div style={{
          marginBottom: '16px',
          fontSize: '10px', fontWeight: '800', letterSpacing: '4px', textTransform: 'uppercase',
          color: glowColor,
          animation: 'rarityLabel 0.4s ease-out 0.3s both',
        }}>
          {rarity === 'legendary' ? '✦ Legendary Badge ✦' : '★ Rare Badge ★'}
        </div>
      )}

      {/* Icon */}
      <div style={{
        animation: 'badgePop 0.7s cubic-bezier(0.34,1.56,0.64,1) 0.2s forwards',
        opacity: 0,
        ...(rarity === 'legendary' ? { animation: 'badgePop 0.7s cubic-bezier(0.34,1.56,0.64,1) 0.2s forwards, legendGlow 2s ease 1s infinite' } : {}),
      }}>
        {IconComp ? <IconComp size={iconSize} earned={true} /> : <span style={{ fontSize: iconSize * 0.8 + 'px' }}>{badge.icon}</span>}
      </div>

      <div style={{ marginTop: '20px', fontSize: '11px', color: glowColor, fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase' }}>
        {rarity === 'legendary' ? 'Legendary Unlocked' : rarity === 'rare' ? 'Rare Badge Unlocked' : 'Badge Unlocked'}
      </div>
      <div style={{ marginTop: '8px', fontSize: rarity==='legendary'?'26px':'22px', fontWeight: '800', color: C.textPrimary }}>
        {badge.label}
      </div>
      <div style={{ marginTop: '4px', fontSize: '13px', color: C.textSecondary }}>
        +{badge.pts} pts
      </div>
      {rarity === 'legendary' && (
        <div style={{ marginTop: '8px', fontSize: '11px', color: glowColor, fontWeight: '600' }}>
          One of the rarest achievements in Bracketz
        </div>
      )}
    </div>
  );
}

// ── BADGE SHOWCASE (trophy shelf) ─────────────────────────────────────────────
function BadgeShowcase({ earnedBadges }) {
  if (earnedBadges.length === 0) return null;
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontSize: '9px', fontWeight: '700', color: C.gold, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span>🏆</span> Your Badges ({earnedBadges.length})
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '10px',
        backgroundColor: C.bgCard,
        borderRadius: '14px',
        border: '1px solid ' + C.goldBorder,
        padding: '14px',
      }}>
        {earnedBadges.map(badge => {
          const IconComp = BADGE_ICONS[badge.key];
          const catColor = CAT_COLORS[badge.category] || C.orange;
          return (
            <div key={badge.key} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
              width: '52px',
            }}>
              <div style={{
                width: '44px', height: '44px',
                borderRadius: '12px',
                backgroundColor: catColor + '1A',
                border: '1px solid ' + catColor + '55',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 10px ' + catColor + '33',
              }}>
                {IconComp ? <IconComp size={26} earned={true} /> : <span style={{ fontSize: '20px' }}>{badge.icon}</span>}
              </div>
              <span style={{ fontSize: '7px', fontWeight: '600', color: C.textMuted, textAlign: 'center', lineHeight: '1.2' }}>
                {badge.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── BADGE CARD ────────────────────────────────────────────────────────────────
function BadgeCard({ badge, earned, prog, isNew }) {
  const IconComp = BADGE_ICONS[badge.key];
  const catColor = CAT_COLORS[badge.category] || C.orange;
  const pct      = Math.round((prog.val / prog.max) * 100);
  const nearDone = !earned && pct >= 80;

  return (
    <div style={{
      borderRadius: '12px',
      border: '1px solid ' + (earned ? catColor + '60' : C.border),
      backgroundColor: earned ? catColor + '0D' : C.bgCard,
      padding: '12px 14px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: earned ? '0 0 16px ' + catColor + '1A' : 'none',
      transition: 'all 0.2s',
    }}>
      {/* Shimmer on earned */}
      {earned && (
        <div style={{
          position: 'absolute', top: 0, left: '-100%',
          width: '60%', height: '100%',
          background: 'linear-gradient(90deg, transparent, ' + catColor + '15, transparent)',
          animation: 'shimmer 3s ease-in-out infinite',
          pointerEvents: 'none',
        }}/>
      )}

      {/* NEW badge flash */}
      {isNew && (
        <div style={{
          position: 'absolute', top: '8px', right: '8px',
          backgroundColor: C.gold, borderRadius: '8px',
          padding: '2px 7px', fontSize: '8px', fontWeight: '800', color: '#000',
        }}>NEW</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Icon */}
        <div style={{
          width: '44px', height: '44px', flexShrink: 0,
          borderRadius: '12px',
          backgroundColor: earned ? catColor + '1A' : 'rgba(255,255,255,0.03)',
          border: '1px solid ' + (earned ? catColor + '44' : 'rgba(255,255,255,0.06)'),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
        }}>
          {IconComp ? <IconComp size={26} earned={earned} /> : (
            <span style={{ fontSize: '20px', opacity: earned ? 1 : 0.25 }}>{badge.icon}</span>
          )}
          {/* Lock overlay for unearned */}
          {!earned && (
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.4)',
            }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <rect x="2" y="5" width="8" height="6" rx="1.5" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
                <path d="M4 5V4a2 2 0 014 0v1" stroke="rgba(255,255,255,0.3)" strokeWidth="1" fill="none"/>
              </svg>
            </div>
          )}
        </div>

        {/* Text */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: earned ? C.textPrimary : C.textMuted, marginBottom: '2px' }}>
            {badge.label}
          </div>
          <div style={{ fontSize: '11px', color: C.textMuted }}>{badge.desc}</div>
          {/* Progress bar for unearned */}
          {!earned && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '10px', color: nearDone ? catColor : C.textMuted, fontWeight: nearDone ? '700' : '400' }}>
                  {nearDone ? '🔥 Almost there!' : prog.val + ' / ' + prog.max}
                </span>
                <span style={{ fontSize: '10px', color: C.textMuted }}>{pct}%</span>
              </div>
              <div style={{ height: '5px', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: pct + '%', height: '100%', borderRadius: '3px',
                  backgroundColor: nearDone ? catColor : C.blue,
                  transition: 'width 0.5s ease',
                  boxShadow: nearDone ? '0 0 6px ' + catColor : 'none',
                  animation: nearDone ? 'almostThere 1.5s ease infinite' : 'none',
                }}/>
              </div>
            </div>
          )}
        </div>

        {/* Right side */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {earned ? (
            <>
              <div style={{
                backgroundColor: catColor + '22',
                border: '1px solid ' + catColor + '55',
                borderRadius: '20px', padding: '3px 10px',
                fontSize: '9px', color: catColor, fontWeight: '700',
                marginBottom: '4px', whiteSpace: 'nowrap',
              }}>✓ Earned</div>
              <div style={{ fontSize: '11px', color: catColor, fontWeight: '700' }}>+{badge.pts} pts</div>
            </>
          ) : (
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '700' }}>+{badge.pts} pts</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ text }) {
  return (
    <div style={{ fontSize:'9px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'8px', paddingBottom:'6px', borderBottom:`1px solid ${C.border}` }}>
      {text}
    </div>
  );
}

function StatCard({ label, value, sub, accent, color }) {
  return (
    <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'12px' }}>
      <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>{label}</div>
      <div style={{ fontSize:'22px', fontWeight:'800', color:color || (accent ? C.orange : C.textPrimary) }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>{sub}</div>}
    </div>
  );
}

function BarRow({ name, pct, seed, isUser }) {
  return (
    <div style={{ marginBottom:'10px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
          {seed && <span style={{ fontSize:'10px', fontWeight:'700', color:isUser ? C.orange : C.textMuted }}>#{seed}</span>}
          <span style={{ fontSize:'12px', color:C.textSecondary }}>{name}</span>
          {isUser && <span style={{ fontSize:'9px', fontWeight:'700', color:C.orange, backgroundColor:C.orangeDim, padding:'2px 6px', borderRadius:'6px' }}>YOU</span>}
        </div>
        <span style={{ fontSize:'12px', fontWeight:'700', color:isUser ? C.orange : C.textMuted }}>{pct}%</span>
      </div>
      <div style={{ backgroundColor:C.bgDeep, borderRadius:'3px', height:'5px', overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', borderRadius:'3px', backgroundColor:isUser ? C.orange : C.blue, transition:'width 0.4s ease' }} />
      </div>
    </div>
  );
}

function LeaderboardRow({ entry, idx, isMe, valueKey, valueSuffix = '', valueLabel }) {
  const medals = ['🥇','🥈','🥉'];
  const medal  = medals[idx] || null;
  return (
    <div style={{ backgroundColor:isMe ? '#1a3a5c' : C.bgCard, borderRadius:'10px', border:`1px solid ${isMe ? C.orangeBorder : C.border}`, padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px' }}>
      <div style={{ width:'28px', textAlign:'center', flexShrink:0 }}>
        {medal ? <span style={{ fontSize:'18px' }}>{medal}</span> : <span style={{ fontSize:'13px', fontWeight:'700', color:C.textMuted }}>#{idx+1}</span>}
      </div>
      <div style={{ width:'32px', height:'32px', borderRadius:'50%', backgroundColor:isMe ? C.orangeDim : C.bgHover, border:`1px solid ${isMe ? C.orangeBorder : C.border}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:'700', color:isMe ? C.orange : C.textSecondary, flexShrink:0 }}>
        {entry.username.charAt(0).toUpperCase()}
      </div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:'13px', fontWeight:'700', color:isMe ? C.textPrimary : C.textSecondary }}>
          {entry.username}
          {isMe && <span style={{ fontSize:'9px', color:C.orange, backgroundColor:C.orangeDim, padding:'2px 6px', borderRadius:'6px', marginLeft:'6px' }}>YOU</span>}
        </div>
        <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>{entry.total} bracket{entry.total !== 1 ? 's' : ''} submitted</div>
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontSize:'16px', fontWeight:'800', color:isMe ? C.orange : C.textPrimary }}>{entry[valueKey]}{valueSuffix}</div>
        <div style={{ fontSize:'9px', color:C.textMuted }}>{valueLabel}</div>
      </div>
    </div>
  );
}

// ── PICK HISTORY CALENDAR ─────────────────────────────────────────────────────
function PickHistory({ allBrackets, userBrackets }) {
  const today     = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const playedDates = new Set(
    userBrackets.map(ub => {
      const b = allBrackets.find(b => b.id === ub.bracketId);
      return b ? b.date : null;
    }).filter(Boolean)
  );

  const todayStr = getLocalDateString();
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
  const daysInMonth  = getDaysInMonth(viewYear, viewMonth);
  const firstDay     = getFirstDayOfMonth(viewYear, viewMonth);

  const goBack = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const goForward = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };

  const isToday  = (day) => { const ds = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`; return ds === todayStr; };
  const isPlayed = (day) => { const ds = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`; return playedDates.has(ds); };
  const isFuture = (day) => { const ds = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`; return ds > todayStr; };

  const monthPlayed = Array.from({ length: daysInMonth }, (_, i) => i + 1).filter(d => isPlayed(d) && !isFuture(d)).length;
  const monthTotal  = Array.from({ length: daysInMonth }, (_, i) => i + 1).filter(d => !isFuture(d)).length;
  const totalPlayed = playedDates.size;

  const calcLongestStreak = () => {
    const sorted = [...playedDates].sort(); let longest = 0, cur = 0;
    for (let i = 0; i < sorted.length; i++) {
      if (i === 0) { cur = 1; continue; }
      const diff = (new Date(sorted[i]) - new Date(sorted[i-1])) / 86400000;
      cur = diff === 1 ? cur + 1 : 1; longest = Math.max(longest, cur);
    }
    return longest;
  };
  const calcCurrentStreak = () => {
    let streak = 0; const check = new Date();
    for (let i = 0; i < 365; i++) { const ds = getLocalDateString(check); if (playedDates.has(ds)) { streak++; check.setDate(check.getDate() - 1); } else break; }
    return streak;
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const canGoForward = !(viewYear === today.getFullYear() && viewMonth === today.getMonth());

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
        <div onClick={goBack} style={{ width:'32px', height:'32px', borderRadius:'50%', backgroundColor:C.bgCard, border:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'16px', color:C.textSecondary }}>‹</div>
        <div style={{ fontSize:'15px', fontWeight:'700', color:C.textPrimary }}>{MONTH_NAMES[viewMonth]} {viewYear}</div>
        <div onClick={canGoForward ? goForward : undefined} style={{ width:'32px', height:'32px', borderRadius:'50%', backgroundColor:canGoForward ? C.bgCard : 'transparent', border:`1px solid ${canGoForward ? C.border : 'transparent'}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:canGoForward ? 'pointer' : 'default', fontSize:'16px', color:canGoForward ? C.textSecondary : 'transparent' }}>›</div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', marginBottom:'8px' }}>
        {DAY_LABELS.map(d => <div key={d} style={{ textAlign:'center', fontSize:'10px', fontWeight:'700', color:C.textMuted, padding:'4px 0' }}>{d}</div>)}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'4px', marginBottom:'20px' }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const played = isPlayed(day), today_ = isToday(day), future = isFuture(day);
          return (
            <div key={day} style={{ aspectRatio:'1', display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
              {today_ && <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:`2px solid ${C.orange}` }} />}
              <div style={{ width:'32px', height:'32px', borderRadius:'50%', backgroundColor: played ? C.orange : future ? 'transparent' : C.bgCard, border: played ? 'none' : future ? 'none' : `1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontSize:'11px', fontWeight: today_ ? '800' : '500', color: played ? '#FFFFFF' : future ? C.bgHover : C.textMuted }}>{day}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ borderTop:`1px solid ${C.border}`, marginBottom:'16px' }} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'16px' }}>
        <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'12px', textAlign:'center' }}>
          <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>This month</div>
          <div style={{ fontSize:'20px', fontWeight:'800', color:C.textPrimary }}>{monthPlayed}<span style={{ fontSize:'11px', color:C.textMuted, fontWeight:'400' }}>/{monthTotal}</span></div>
        </div>
        <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.orangeBorder}`, padding:'12px', textAlign:'center' }}>
          <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>Current 🔥</div>
          <div style={{ fontSize:'20px', fontWeight:'800', color:C.orange }}>{calcCurrentStreak()}</div>
        </div>
        <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'12px', textAlign:'center' }}>
          <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>Best streak</div>
          <div style={{ fontSize:'20px', fontWeight:'800', color:C.textPrimary }}>{calcLongestStreak()}</div>
        </div>
      </div>
      <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ fontSize:'13px', color:C.textSecondary }}>All-time brackets played</div>
        <div style={{ fontSize:'20px', fontWeight:'800', color:C.textPrimary }}>{totalPlayed}</div>
      </div>
      <div style={{ display:'flex', gap:'16px', marginTop:'14px', justifyContent:'center' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}><div style={{ width:'12px', height:'12px', borderRadius:'50%', backgroundColor:C.orange }} /><span style={{ fontSize:'10px', color:C.textMuted }}>Played</span></div>
        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}><div style={{ width:'12px', height:'12px', borderRadius:'50%', backgroundColor:C.bgCard, border:`1px solid ${C.border}` }} /><span style={{ fontSize:'10px', color:C.textMuted }}>Missed</span></div>
        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}><div style={{ width:'12px', height:'12px', borderRadius:'50%', border:`2px solid ${C.orange}` }} /><span style={{ fontSize:'10px', color:C.textMuted }}>Today</span></div>
      </div>
      {totalPlayed === 0 && <div style={{ textAlign:'center', marginTop:'24px', color:C.textMuted, fontSize:'13px' }}>Submit your first bracket to start filling in your calendar!</div>}
    </div>
  );
}

// ── MY STATS ──────────────────────────────────────────────────────────────────
function MyStats({ user, allBrackets, userBrackets, userRank, totalUsers, userPts }) {
  const [crowdAgreement,    setCrowdAgreement]    = useState(null);
  const [winRate,           setWinRate]           = useState(null);
  const [contrarianScore,   setContrarianScore]   = useState(null);
  const [mostPublicBracket, setMostPublicBracket] = useState(null);
  const [loadingCrowd,      setLoadingCrowd]      = useState(true);
  const [commentCount,      setCommentCount]      = useState(0);
  const [activeCat,         setActiveCat]         = useState('Participation');
  const [unlockQueue,       setUnlockQueue]       = useState([]);
  const [newKeys,           setNewKeys]           = useState(new Set());
  const prevEarnedKeysRef = useRef(null);
  const BADGE_CATS = ['Participation','Social','Accuracy','Explorer'];

  useEffect(() => {
    if (userBrackets.length === 0) { setLoadingCrowd(false); return; }
    calcCrowdStats(); loadComments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userBrackets]);

  const loadComments = async () => {
    try { const snap = await getDocs(query(collection(db, 'comments'), where('userId', '==', user.uid))); setCommentCount(snap.size); } catch(e) {}
  };

  const calcStreak = () => {
    const dates = new Set(userBrackets.map(ub => allBrackets.find(b => b.id === ub.bracketId)?.date).filter(Boolean));
    let streak = 0; const check = new Date();
    for (let i = 0; i < 365; i++) { const ds = getLocalDateString(check); if (dates.has(ds)) { streak++; check.setDate(check.getDate() - 1); } else break; }
    return streak;
  };

  const calcLongestStreak = () => {
    const dates = [...new Set(userBrackets.map(ub => allBrackets.find(b => b.id === ub.bracketId)?.date).filter(Boolean))].sort();
    let longest = 0, cur = 0;
    for (let i = 0; i < dates.length; i++) {
      if (i === 0) { cur = 1; continue; }
      const diff = (new Date(dates[i]) - new Date(dates[i-1])) / 86400000;
      cur = diff === 1 ? cur + 1 : 1; longest = Math.max(longest, cur);
    }
    return Math.max(longest, calcStreak());
  };

  const calcMonthly = () => {
    const now = new Date();
    return userBrackets.filter(ub => { const b = allBrackets.find(b => b.id === ub.bracketId); if (!b) return false; const d = new Date(b.date); return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth(); }).length;
  };

  const calcPerfectWeek = () => {
    const dates = new Set(userBrackets.map(ub => allBrackets.find(b => b.id === ub.bracketId)?.date).filter(Boolean));
    const sorted = [...dates].sort();
    for (let i = 0; i <= sorted.length - 7; i++) {
      let consecutive = 1;
      for (let j = i + 1; j < sorted.length && consecutive < 7; j++) { const diff = (new Date(sorted[j]) - new Date(sorted[j-1])) / 86400000; if (diff === 1) consecutive++; else break; }
      if (consecutive >= 7) return 1;
    }
    return 0;
  };

  const calcByCategory = () => {
    const map = {};
    userBrackets.forEach(ub => { const b = allBrackets.find(b => b.id === ub.bracketId); if (b?.keyword) map[b.keyword] = (map[b.keyword] || 0) + 1; });
    return map;
  };

  const calcCrowdStats = async () => {
    try {
      const allUBSnap = await getDocs(query(collection(db, 'userBrackets'), where('isSubmitted', '==', true)));
      const allUBs    = allUBSnap.docs.map(d => ({ id:d.id, ...d.data() }));
      const ubsByBracket = {};
      allUBs.forEach(ub => { if (!ubsByBracket[ub.bracketId]) ubsByBracket[ub.bracketId] = []; ubsByBracket[ub.bracketId].push(ub.picks || {}); });
      let totalAgreement = 0, agreementCount = 0, bestAgreement = 0, bestBracket = null;
      let winCount = 0, contrarianCount = 0, totalWithData = 0;
      for (const ub of userBrackets) {
        const bracketPicks = ubsByBracket[ub.bracketId] || [];
        if (bracketPicks.length < 2) continue;
        const bracket = allBrackets.find(b => b.id === ub.bracketId); if (!bracket) continue;
        totalWithData++;
        const keys = Object.keys(ub.picks || {}); let matches = 0;
        for (const key of keys) { if (bracketPicks.filter(p => p[key] === ub.picks[key]).length / bracketPicks.length >= 0.5) matches++; }
        const agreement = keys.length > 0 ? Math.round((matches / keys.length) * 100) : 0;
        totalAgreement += agreement; agreementCount++;
        if (agreement > bestAgreement) { bestAgreement = agreement; bestBracket = { ...bracket, agreement }; }
        const champCounts = {};
        bracketPicks.forEach(p => { const c = p['r4_m0']; if (c) champCounts[c] = (champCounts[c]||0)+1; });
        const topChamp = Object.entries(champCounts).sort((a,b) => b[1]-a[1])[0]?.[0];
        const myChamp  = ub.picks?.['r4_m0'];
        if (myChamp && topChamp) {
          if (myChamp === topChamp) winCount++;
          const myChampPct = Math.round((champCounts[myChamp]||0) / bracketPicks.length * 100);
          if (myChampPct < 50) contrarianCount++;
        }
      }
      setCrowdAgreement(agreementCount > 0 ? Math.round(totalAgreement / agreementCount) : null);
      setMostPublicBracket(bestBracket);
      setWinRate(totalWithData > 0 ? Math.round((winCount / totalWithData) * 100) : null);
      setContrarianScore(totalWithData > 0 ? Math.round((contrarianCount / totalWithData) * 100) : null);
    } catch(e) { console.error(e); }
    setLoadingCrowd(false);
  };

  const total            = userBrackets.length;
  const streak           = calcStreak();
  const longestStreak    = calcLongestStreak();
  const monthly          = calcMonthly();
  const perfectWeek      = calcPerfectWeek();
  const byCategory       = calcByCategory();
  const categoriesPlayed = Object.keys(byCategory).length;
  const stats = { total, streak, monthly, perfectWeek, byCategory, categoriesPlayed, comments:commentCount, maxLikes:0, contrarian:contrarianScore !== null ? contrarianScore : 0, crowdMatch: crowdAgreement !== null && crowdAgreement >= 80 ? 1 : 0, oracle:0 };
  const earnedBadges  = BADGES.filter(b => b.req(stats));
  const earnedKeys    = new Set(earnedBadges.map(b => b.key));
  const totalPts      = earnedBadges.reduce((sum, b) => sum + b.pts, 0);
  const earnedKeysStr = [...earnedKeys].sort().join(',');

  useEffect(() => {
    // Use localStorage to track which badges have already been animated
    // This persists across tab switches and component remounts
    const storageKey = `bracketz_animated_badges_${user.uid}`;
    const alreadyAnimated = new Set(
      (localStorage.getItem(storageKey) || '').split(',').filter(Boolean)
    );

    // If this is the first time we're tracking (empty storage), mark all current badges as already animated
    if (alreadyAnimated.size === 0 && earnedKeys.size > 0) {
      const allKeys = [...earnedKeys].join(',');
      localStorage.setItem(storageKey, allKeys);
      return;
    }
    // Find badges earned now that haven't been animated yet
    const newly = [...earnedKeys].filter(k => !alreadyAnimated.has(k));

    if (newly.length > 0) {
      setNewKeys(new Set(newly));
      const badgesToShow = newly.map(k => BADGES.find(b => b.key === k)).filter(Boolean);
      setUnlockQueue(badgesToShow);

      // Mark them as animated immediately so remounts don't re-fire
      const updated = [...alreadyAnimated, ...newly].join(',');
      localStorage.setItem(storageKey, updated);

      // Send notifications for newly earned badges
      for (const key of newly) {
        const badge = BADGES.find(b => b.key === key);
        if (badge) addDoc(collection(db, 'notifications'), { userId:user.uid, type:'badge_earned', title:`Badge Earned: ${badge.label} 🏅`, message:`You just earned the ${badge.label} badge. Keep it up!`, read:false, createdAt:serverTimestamp() }).catch(err => console.error(err));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earnedKeysStr, user.uid]);

  const getContrarianLabel = (score) => {
    if (score === null) return null;
    if (score >= 70) return { label:'Hardcore Contrarian', color:'#F59E0B' };
    if (score >= 50) return { label:'Contrarian Picker',   color:'#F59E0B' };
    if (score >= 30) return { label:'Occasional Rebel',    color:C.textSecondary };
    return { label:'Crowd Follower', color:C.green };
  };
  const contrarianLabel = getContrarianLabel(contrarianScore);

  return (
    <div>
      <style>{`
        @keyframes shimmer { 0%{left:-100%} 100%{left:200%} }
      `}</style>

      {/* Badge unlock animation */}
      {unlockQueue.length > 0 && (
        <BadgeUnlockBurst
          badge={unlockQueue[0]}
          onDone={() => setUnlockQueue(q => q.slice(1))}
        />
      )}

      {/* Badge showcase — trophy shelf */}
      {earnedBadges.length > 0 && <BadgeShowcase earnedBadges={earnedBadges} />}

      <SectionLabel text="Performance" />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'12px' }}>
        <StatCard label="Brackets submitted" value={total}                                                                        sub="all time" />
        <StatCard label="Current streak"     value={`${streak} 🔥`}                                                              sub="days in a row" accent />
        <StatCard label="Longest streak"     value={longestStreak}                                                                sub="personal best" />
        <StatCard label="Vs. the crowd"      value={loadingCrowd ? '...' : crowdAgreement !== null ? `${crowdAgreement}%` : '—'} sub="avg agreement" accent />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'16px' }}>
        <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'12px' }}>
          <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>Win Rate 🏆</div>
          <div style={{ fontSize:'22px', fontWeight:'800', color:C.green }}>{loadingCrowd ? '...' : winRate !== null ? `${winRate}%` : '—'}</div>
          <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>champion matched crowd</div>
        </div>
        <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'12px' }}>
          <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>Contrarian 🎯</div>
          <div style={{ fontSize:'22px', fontWeight:'800', color:'#F59E0B' }}>{loadingCrowd ? '...' : contrarianScore !== null ? `${contrarianScore}%` : '—'}</div>
          <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>picks vs majority</div>
        </div>
      </div>

      {contrarianLabel && !loadingCrowd && (
        <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'14px 16px', marginBottom:'16px', display:'flex', alignItems:'center', gap:'14px' }}>
          <div style={{ fontSize:'28px' }}>{contrarianScore >= 50 ? '🎯' : '👥'}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'10px', color:C.textMuted, fontWeight:'600', letterSpacing:'1px', textTransform:'uppercase', marginBottom:'3px' }}>Your Picker Identity</div>
            <div style={{ fontSize:'16px', fontWeight:'800', color:contrarianLabel.color }}>{contrarianLabel.label}</div>
            <div style={{ fontSize:'11px', color:C.textMuted, marginTop:'2px' }}>
              {contrarianScore >= 50 ? `You go against the crowd ${contrarianScore}% of the time` : `You agree with the crowd ${100 - (contrarianScore||0)}% of the time`}
            </div>
          </div>
        </div>
      )}

      <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.orangeBorder}`, padding:'14px 16px', marginBottom:'20px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'3px', fontWeight:'600', letterSpacing:'1px', textTransform:'uppercase' }}>Total Points</div>
          <div style={{ fontSize:'28px', fontWeight:'800', color:C.orange, lineHeight:1 }}>{totalPts} <span style={{ fontSize:'13px', color:C.textMuted, fontWeight:'400' }}>/ {TOTAL_POSSIBLE_PTS}</span></div>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'6px' }}>{earnedBadges.length} of {BADGES.length} badges</div>
          <div style={{ backgroundColor:C.bgDeep, borderRadius:'4px', height:'6px', width:'120px', overflow:'hidden' }}>
            <div style={{ width:`${Math.round((totalPts / TOTAL_POSSIBLE_PTS) * 100)}%`, height:'100%', backgroundColor:C.orange, borderRadius:'4px', transition:'width 0.4s ease' }} />
          </div>
        </div>
      </div>

      {/* Badges section */}
      <SectionLabel text="Badges" />
      <div style={{ display:'flex', gap:'6px', marginBottom:'12px', overflowX:'auto', scrollbarWidth:'none' }}>
        {BADGE_CATS.map(cat => (
          <div key={cat} onClick={() => setActiveCat(cat)} style={{
            fontSize:'10px', fontWeight:'700', padding:'5px 12px', borderRadius:'20px',
            whiteSpace:'nowrap', cursor:'pointer', flexShrink:0,
            backgroundColor: activeCat === cat ? (CAT_COLORS[cat] || C.orange) : 'transparent',
            color: activeCat === cat ? '#FFF' : C.textMuted,
            border: `1px solid ${activeCat === cat ? (CAT_COLORS[cat] || C.orange) : 'rgba(255,255,255,0.1)'}`,
          }}>
            {cat}
          </div>
        ))}
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px' }}>
        {BADGES.filter(b => b.category === activeCat).map(badge => {
          const earned = earnedKeys.has(badge.key);
          const prog   = badge.progress(stats);
          const isNew  = newKeys.has(badge.key);
          return <BadgeCard key={badge.key} badge={badge} earned={earned} prog={prog} isNew={isNew} />;
        })}
      </div>

      {mostPublicBracket && (
        <>
          <SectionLabel text="Most public bracket" />
          <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.orangeBorder}`, padding:'12px', marginBottom:'16px' }}>
            <div style={{ fontSize:'9px', color:'rgba(255,107,0,0.7)', fontWeight:'700', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'4px' }}>Closest to the crowd</div>
            <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, marginBottom:'4px' }}>{mostPublicBracket.category}</div>
            <div style={{ fontSize:'11px', color:C.textMuted }}>Your picks matched {mostPublicBracket.agreement}% of all submissions</div>
          </div>
        </>
      )}

      {userRank && (
        <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'14px 16px', marginBottom:'16px', display:'flex', alignItems:'center', gap:'14px' }}>
          <div style={{ fontSize:'32px' }}>{userRank===1?'🥇':userRank===2?'🥈':userRank===3?'🥉':'🥳'}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'10px', color:C.textMuted, fontWeight:'600', letterSpacing:'1px', textTransform:'uppercase', marginBottom:'3px' }}>Global Ranking</div>
            <div style={{ fontSize:'20px', fontWeight:'800', color:C.textPrimary, lineHeight:1 }}>
              #{userRank} <span style={{ fontSize:'12px', color:C.textMuted, fontWeight:'400' }}>of {totalUsers} player{totalUsers !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:'22px', fontWeight:'800', color:C.orange }}>{userPts}</div>
            <div style={{ fontSize:'10px', color:C.textMuted }}>pts</div>
          </div>
        </div>
      )}

      {total === 0 && (
        <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'24px', textAlign:'center' }}>
          <div style={{ fontSize:'32px', marginBottom:'10px' }}>📊</div>
          <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, marginBottom:'5px' }}>No stats yet</div>
          <div style={{ fontSize:'12px', color:C.textMuted }}>Submit your first bracket to start tracking.</div>
        </div>
      )}
    </div>
  );
}

// ── COMMUNITY ─────────────────────────────────────────────────────────────────
function Community({ allBrackets, timeFilter, categoryFilter, currentUserId }) {
  const [loading,           setLoading]           = useState(true);
  const [totalSubmissions,  setTotalSubmissions]  = useState(0);
  const [todaySubmissions,  setTodaySubmissions]  = useState(0);
  const [championsData,     setChampionsData]     = useState([]);
  const [contestedMatch,    setContestedMatch]    = useState(null);
  const [ptsLeaderboard,    setPtsLeaderboard]    = useState([]);
  const [streakLeaderboard, setStreakLeaderboard] = useState([]);
  const [lbTab,             setLbTab]             = useState('streak');
  const today = getLocalDateString();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [timeFilter, categoryFilter]);

  const calcUserPoints = (userUBs, allBracketsData) => {
    const submitted = userUBs.filter(ub => ub.isSubmitted);
    const total     = submitted.length;
    const dates     = new Set(submitted.map(ub => allBracketsData.find(b => b.id === ub.bracketId)?.date).filter(Boolean));
    let streak = 0; const check = new Date();
    for (let i = 0; i < 365; i++) { const ds = getLocalDateString(check); if (dates.has(ds)) { streak++; check.setDate(check.getDate()-1); } else break; }
    const now = new Date();
    const monthly = submitted.filter(ub => { const b = allBracketsData.find(b => b.id === ub.bracketId); if (!b) return false; const d = new Date(b.date); return d.getFullYear()===now.getFullYear() && d.getMonth()===now.getMonth(); }).length;
    const byCategory = {};
    submitted.forEach(ub => { const b = allBracketsData.find(b => b.id === ub.bracketId); if (b?.keyword) byCategory[b.keyword] = (byCategory[b.keyword]||0)+1; });
    const categoriesPlayed = Object.keys(byCategory).length;
    const stats = { total, streak, monthly, perfectWeek:0, byCategory, categoriesPlayed, comments:0, maxLikes:0, contrarian:0, crowdMatch:0, oracle:0 };
    return BADGES.filter(b => b.req(stats)).reduce((sum, b) => sum + b.pts, 0);
  };

  const calcUserStreak = (submittedUBs, allBracketsData) => {
    const dates = new Set(submittedUBs.map(ub => allBracketsData.find(b => b.id === ub.bracketId)?.date).filter(Boolean));
    let streak = 0; const check = new Date();
    for (let i = 0; i < 365; i++) { const ds = getLocalDateString(check); if (dates.has(ds)) { streak++; check.setDate(check.getDate()-1); } else break; }
    return streak;
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const allUBSnap = await getDocs(query(collection(db, 'userBrackets'), where('isSubmitted', '==', true)));
      const allUBs    = allUBSnap.docs.map(d => ({ id:d.id, ...d.data() }));
      let filtered = [...allBrackets];
      if (categoryFilter !== 'All') filtered = filtered.filter(b => b.keyword === categoryFilter);
      if (timeFilter === 'This month') { const start = new Date(); start.setDate(1); filtered = filtered.filter(b => b.date >= getLocalDateString(start)); }
      else if (timeFilter === 'This week') { const start = new Date(); start.setDate(start.getDate() - 7); filtered = filtered.filter(b => b.date >= getLocalDateString(start)); }
      const relevantIds = new Set(filtered.map(b => b.id));
      const relevantUBs = allUBs.filter(ub => relevantIds.has(ub.bracketId));
      const total       = relevantUBs.length;
      const todayBracket = allBrackets.find(b => b.date === today);
      const todayCount   = todayBracket ? relevantUBs.filter(ub => ub.bracketId === todayBracket.id).length : 0;
      const championCounts = {};
      relevantUBs.forEach(ub => { const champ = ub.picks?.['r4_m0']; if (champ) championCounts[champ] = (championCounts[champ] || 0) + 1; });
      const sortedChamps = Object.entries(championCounts).sort((a, b) => b[1]-a[1]).slice(0, 5).map(([name, count]) => ({ name, pct: total > 0 ? Math.round((count/total)*100) : 0 }));
      const ubsByBracket = {};
      relevantUBs.forEach(ub => { if (!ubsByBracket[ub.bracketId]) ubsByBracket[ub.bracketId] = []; ubsByBracket[ub.bracketId].push(ub.picks || {}); });
      let mostContested = null, smallestSplit = 1;
      Object.entries(ubsByBracket).forEach(([bracketId, bracketPicks]) => {
        if (bracketPicks.length < 2) return;
        const bracket = allBrackets.find(b => b.id === bracketId); if (!bracket) return;
        for (let r = 0; r < 5; r++) {
          const matchCount = [16,8,4,2,1][r];
          for (let m = 0; m < matchCount; m++) {
            const key = `r${r}_m${m}`; const votes = {};
            bracketPicks.forEach(picks => { const v = picks[key]; if (v) votes[v] = (votes[v]||0)+1; });
            const teams = Object.keys(votes);
            if (teams.length === 2) {
              const split = Math.abs(votes[teams[0]]-votes[teams[1]]) / bracketPicks.length;
              if (split < smallestSplit) { smallestSplit = split; const pct0 = Math.round((votes[teams[0]]/bracketPicks.length)*100); mostContested = { bracket:bracket.category, team1:teams[0], pct1:pct0, team2:teams[1], pct2:100-pct0, round:['Round of 32','Round of 16','Round of 8','Round of 4','Championship'][r] }; }
            }
          }
        }
      });
      const byUser = {};
      allUBs.forEach(ub => { if (!byUser[ub.userId]) byUser[ub.userId] = { userId:ub.userId, username:ub.userId.slice(0,8), brackets:[] }; byUser[ub.userId].brackets.push(ub); });
      try { const profilesSnap = await getDocs(collection(db, 'userProfiles')); profilesSnap.docs.forEach(d => { const data = d.data(); if (byUser[d.id] && data.username) byUser[d.id].username = data.username; }); } catch(e) {}
      const users = Object.values(byUser).map(u => { const submitted = u.brackets.filter(ub => ub.isSubmitted); return { userId:u.userId, username:u.username, total:submitted.length, pts:calcUserPoints(u.brackets, allBrackets), streak:calcUserStreak(submitted, allBrackets) }; });
      setPtsLeaderboard([...users].sort((a,b) => b.pts-a.pts).slice(0,10));
      setStreakLeaderboard([...users].sort((a,b) => b.streak-a.streak).slice(0,10));
      setTotalSubmissions(total); setTodaySubmissions(todayCount); setChampionsData(sortedChamps); setContestedMatch(mostContested);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (loading) return <div style={{ textAlign:'center', padding:'40px', color:C.textMuted, fontSize:'13px' }}>Loading community data...</div>;
  const activeLeaderboard = lbTab === 'streak' ? streakLeaderboard : ptsLeaderboard;

  return (
    <div>
      <SectionLabel text="Total submissions" />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'16px' }}>
        <StatCard label="All time" value={totalSubmissions.toLocaleString()} sub="across all brackets" />
        <StatCard label="Today"    value={todaySubmissions}                  sub="today's bracket" accent />
      </div>
      {championsData.length > 0 && (<><SectionLabel text="Most picked champions" /><div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'12px 14px', marginBottom:'16px' }}>{championsData.map(c => <BarRow key={c.name} name={c.name} pct={c.pct} />)}</div></>)}
      {contestedMatch && (<><SectionLabel text="Most contested matchup" /><div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.orangeBorder}`, padding:'12px', marginBottom:'16px' }}><div style={{ fontSize:'9px', color:'rgba(255,107,0,0.7)', fontWeight:'700', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'4px' }}>Closest split</div><div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, marginBottom:'3px' }}>{contestedMatch.team1} vs. {contestedMatch.team2}</div><div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'10px' }}>{contestedMatch.pct1}% vs. {contestedMatch.pct2}% · {contestedMatch.round} · {contestedMatch.bracket}</div><div style={{ display:'flex', gap:'2px', height:'6px' }}><div style={{ width:`${contestedMatch.pct1}%`, backgroundColor:C.orange, borderRadius:'3px 0 0 3px' }} /><div style={{ width:`${contestedMatch.pct2}%`, backgroundColor:C.blue, borderRadius:'0 3px 3px 0' }} /></div></div></>)}
      {activeLeaderboard.length > 0 && (<><SectionLabel text="Leaderboard" /><div style={{ display:'flex', backgroundColor:C.bgDeep, borderRadius:'8px', padding:'3px', marginBottom:'12px', border:`1px solid ${C.border}` }}>{[{ key:'streak', label:'🔥 Streak' },{ key:'points', label:'⚡ Points' }].map(t => (<div key={t.key} onClick={() => setLbTab(t.key)} style={{ flex:1, padding:'7px', textAlign:'center', fontSize:'11px', fontWeight:'700', borderRadius:'6px', cursor:'pointer', backgroundColor:lbTab===t.key?C.orange:'transparent', color:lbTab===t.key?'#FFF':C.textMuted, transition:'all 0.15s' }}>{t.label}</div>))}</div><div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'16px' }}>{activeLeaderboard.map((entry, idx) => (<LeaderboardRow key={entry.userId} entry={entry} idx={idx} isMe={entry.userId === currentUserId} valueKey={lbTab === 'streak' ? 'streak' : 'pts'} valueSuffix={lbTab === 'streak' ? ' 🔥' : ''} valueLabel={lbTab === 'streak' ? 'day streak' : 'pts'} />))}</div></>)}
      {totalSubmissions === 0 && (<div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'24px', textAlign:'center' }}><div style={{ fontSize:'32px', marginBottom:'10px' }}>🌍</div><div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, marginBottom:'5px' }}>No data yet</div><div style={{ fontSize:'12px', color:C.textMuted }}>Stats will appear as more brackets are submitted.</div></div>)}
    </div>
  );
}

// ── METRICS ───────────────────────────────────────────────────────────────────
function Metrics({ user }) {
  const [view,           setView]           = useState('mine');
  const [timeFilter,     setTimeFilter]     = useState('All time');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [loading,        setLoading]        = useState(true);
  const [allBrackets,    setAllBrackets]    = useState([]);
  const [userBrackets,   setUserBrackets]   = useState([]);
  const [userRank,       setUserRank]       = useState(null);
  const [totalUsers,     setTotalUsers]     = useState(null);
  const [userPts,        setUserPts]        = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBase(); }, []);

  const loadBase = async () => {
    try {
      const allB    = await getAllBrackets();
      const ubSnap  = await getDocs(query(collection(db, 'userBrackets'), where('userId', '==', user.uid), where('isSubmitted', '==', true)));
      const userUBs = ubSnap.docs.map(d => ({ id:d.id, ...d.data() }));
      setAllBrackets(allB); setUserBrackets(userUBs);
      await computeRank(user.uid, userUBs, allB);
      setLoading(false);
    } catch (err) { console.error(err); setLoading(false); }
  };

  const computeRank = async (uid, userUBs, allB) => {
    try {
      const allUBSnap = await getDocs(collection(db, 'userBrackets'));
      const allUBs    = allUBSnap.docs.map(d => ({ id:d.id, ...d.data() }));
      const byUser    = {};
      allUBs.forEach(ub => { if (!byUser[ub.userId]) byUser[ub.userId] = []; byUser[ub.userId].push(ub); });
      const calcPts = (ubs) => {
        const submitted = ubs.filter(ub => ub.isSubmitted); const total = submitted.length;
        const dates = new Set(submitted.map(ub => allB.find(b => b.id === ub.bracketId)?.date).filter(Boolean));
        let streak = 0; const check = new Date();
        for (let i = 0; i < 365; i++) { const ds = getLocalDateString(check); if (dates.has(ds)) { streak++; check.setDate(check.getDate()-1); } else break; }
        const now = new Date();
        const monthly = submitted.filter(ub => { const b = allB.find(b => b.id === ub.bracketId); if (!b) return false; const d = new Date(b.date); return d.getFullYear()===now.getFullYear() && d.getMonth()===now.getMonth(); }).length;
        const byCategory = {}; submitted.forEach(ub => { const b = allB.find(b => b.id === ub.bracketId); if (b?.keyword) byCategory[b.keyword]=(byCategory[b.keyword]||0)+1; });
        const categoriesPlayed = Object.keys(byCategory).length;
        const stats = { total, streak, monthly, perfectWeek:0, byCategory, categoriesPlayed, comments:0, maxLikes:0, contrarian:0, crowdMatch:0, oracle:0 };
        return BADGES.filter(b => b.req(stats)).reduce((sum, b) => sum + b.pts, 0);
      };
      const scores = Object.entries(byUser).map(([userId, ubs]) => ({ userId, pts:calcPts(ubs) })).sort((a,b) => b.pts-a.pts);
      const rank   = scores.findIndex(s => s.userId === uid) + 1;
      const myPts  = scores.find(s => s.userId === uid)?.pts || 0;
      setUserRank(rank || 1); setTotalUsers(scores.length); setUserPts(myPts);
    } catch(err) { console.error(err); }
  };

  const TABS = [{ key:'mine', label:'My Stats' }, { key:'community', label:'Community' }, { key:'history', label:'History' }];

  return (
    <div style={{ padding:'16px', fontFamily:font }}>
      <div style={{ marginBottom:'16px' }}>
        <div style={{ fontSize:'22px', fontWeight:'800', color:C.textPrimary }}>Metrics</div>
        <div style={{ fontSize:'12px', color:C.textMuted, marginTop:'3px' }}>Your stats &amp; community data</div>
      </div>
      <div style={{ display:'flex', backgroundColor:C.bgDeep, borderRadius:'8px', padding:'3px', marginBottom:'16px', border:`1px solid ${C.border}` }}>
        {TABS.map(t => (<div key={t.key} onClick={() => setView(t.key)} style={{ flex:1, padding:'7px', textAlign:'center', fontSize:'11px', fontWeight:'700', borderRadius:'6px', letterSpacing:'0.5px', cursor:'pointer', backgroundColor:view===t.key?C.orange:'transparent', color:view===t.key?'#FFF':C.textMuted, transition:'all 0.15s' }}>{t.label}</div>))}
      </div>
      {view === 'community' && (
        <div style={{ marginBottom:'14px' }}>
          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'8px' }}>
            {TIME_FILTERS.map(f => (<div key={f} onClick={() => setTimeFilter(f)} style={{ fontSize:'10px', padding:'4px 10px', borderRadius:'20px', border:`1px solid ${timeFilter===f?C.orangeBorder:'rgba(255,255,255,0.1)'}`, color:timeFilter===f?C.orange:C.textMuted, fontWeight:'600', cursor:'pointer', backgroundColor:timeFilter===f?C.orangeDim:'transparent' }}>{f}</div>))}
          </div>
          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', overflowX:'auto', scrollbarWidth:'none' }}>
            {CATEGORY_FILTERS.map(f => (<div key={f} onClick={() => setCategoryFilter(f)} style={{ fontSize:'10px', padding:'4px 10px', borderRadius:'20px', border:`1px solid ${categoryFilter===f?C.orangeBorder:'rgba(255,255,255,0.1)'}`, color:categoryFilter===f?C.orange:C.textMuted, fontWeight:'600', cursor:'pointer', backgroundColor:categoryFilter===f?C.orangeDim:'transparent', whiteSpace:'nowrap', flexShrink:0 }}>{f}</div>))}
          </div>
        </div>
      )}
      {loading ? (
        <div style={{ textAlign:'center', padding:'40px', color:C.textMuted, fontSize:'13px' }}>Loading...</div>
      ) : view === 'mine' ? (
        <MyStats user={user} allBrackets={allBrackets} userBrackets={userBrackets} userRank={userRank} totalUsers={totalUsers} userPts={userPts} />
      ) : view === 'community' ? (
        <Community allBrackets={allBrackets} timeFilter={timeFilter} categoryFilter={categoryFilter} currentUserId={user.uid} />
      ) : (
        <PickHistory allBrackets={allBrackets} userBrackets={userBrackets} />
      )}
      <div style={{ height:'20px' }} />
    </div>
  );
}

export default Metrics;
